import { AgTooltipHeaderComponent } from 'components/AgGrid/AgTooltipHeaderComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import {
    uerWorkOrderChangeUsersSelectionListCodeFilter,
    uerWorkOrderChangeUsersSelectionListGroupFilter,
    uerWorkOrderChangeUsersSelectionListNameFilter,
    uerWorkOrderChangeUsersSelectionListSectionFilter,
} from 'pages/Project/UerProject/WorkOrderChange/AddWorkOrderChangeUer/workOrderChangeAddPricesQueries';
import { Checkbox, Loader } from 'uikit';
import { formatNumberToRu } from 'utils';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import type { ICheckedPricesCellRendererProps, IPriceTableColumnsProps } from '../../../helpersAndTypes';
import {
    uerWorkOrderUsersSelectionListCodeFilter,
    uerWorkOrderUsersSelectionListGroupFilter,
    uerWorkOrderUsersSelectionListNameFilter,
    uerWorkOrderUsersSelectionListSectionFilter,
} from '../priceTableQueries';

const CheckboxRenderer = (props: ICheckedPricesCellRendererProps) => {
    const { checkedPrices, data, onChangeCheckedPrices } = props;

    const onChangeCreator = useCallback(
        (id: number) =>
            ({ checked }: { checked: boolean }) => {
                onChangeCheckedPrices({
                    [id]: checked,
                });
            },
        [onChangeCheckedPrices],
    );

    if (!data) {
        return <Loader />;
    }

    return <Checkbox onChange={onChangeCreator(data.id)} checked={checkedPrices[data.id]} />;
};
const customHeaderName = () => (
    <span>
        Стоимость
        <div style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            единицы УЕР в текущем уровне цен (с учетом прочих и лимитированных затрат), руб.без НДС
        </div>
    </span>
);

export const useTableColumns = (props: IPriceTableColumnsProps) => {
    const {
        onChangeCheckedPrices,
        checkedPrices,
        extraColumnDefs = [],
        changeFilterHandler,
        filterVariables,
        isOrderChange,
    } = props;

    return useMemo<ColDef[]>(
        () => [
            {
                field: 'menu' as any,
                headerName: '',
                width: 55,
                resizable: false,
                pinned: 'left',
                lockPosition: 'left',
                cellRenderer: CheckboxRenderer,
                cellRendererParams: {
                    checkedPrices,
                    onChangeCheckedPrices,
                },
            },
            {
                field: 'sectionName',
                headerName: 'Раздел',
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: isOrderChange
                        ? 'uerWorkOrderChangeUsersSelectionListSectionFilter'
                        : 'uerWorkOrderUsersSelectionListSectionFilter',
                    query: isOrderChange
                        ? uerWorkOrderChangeUsersSelectionListSectionFilter
                        : uerWorkOrderUsersSelectionListSectionFilter,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: '',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                field: 'groupFullName',
                headerName: 'Подраздел',
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    whereVariables: ['value'],
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: isOrderChange
                        ? 'uerWorkOrderChangeUsersSelectionListGroupFilter'
                        : 'uerWorkOrderUsersSelectionListGroupFilter',
                    query: isOrderChange
                        ? uerWorkOrderChangeUsersSelectionListGroupFilter
                        : uerWorkOrderUsersSelectionListGroupFilter,
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: '',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                field: 'code',
                headerName: 'Шифр',
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    whereVariables: ['value'],
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: isOrderChange
                        ? 'uerWorkOrderChangeUsersSelectionListCodeFilter'
                        : 'uerWorkOrderUsersSelectionListCodeFilter',
                    query: isOrderChange
                        ? uerWorkOrderChangeUsersSelectionListCodeFilter
                        : uerWorkOrderUsersSelectionListCodeFilter,
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: '',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                field: 'name',
                headerName: 'Наименование',
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    whereVariables: ['value'],
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: isOrderChange
                        ? 'uerWorkOrderChangeUsersSelectionListNameFilter'
                        : 'uerWorkOrderUsersSelectionListNameFilter',
                    query: isOrderChange
                        ? uerWorkOrderChangeUsersSelectionListNameFilter
                        : uerWorkOrderUsersSelectionListNameFilter,
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: '',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                field: 'unit',
                headerName: 'Ед.изм',
                width: 90,
            },
            {
                field: 'unitCost',
                headerComponent: AgTooltipHeaderComponent,
                headerComponentParams: {
                    customHeaderName,
                    displayName:
                        'Стоимость единицы УЕР в текущем уровне цен (с учетом прочих и лимитированных затрат), руб.без НДС',
                },
                flex: 1,
                valueFormatter: ({ value }) => formatNumberToRu(value, 2),
                type: 'rightAligned',
                headerClass: 'center-aligned-header',
            },
            ...extraColumnDefs,
        ],
        [changeFilterHandler, checkedPrices, extraColumnDefs, filterVariables, isOrderChange, onChangeCheckedPrices],
    );
};

CheckboxRenderer.displayName = 'CheckboxRenderer';
